import {
  EEventContextAction,
  EEventType,
  type IPhoto,
  type IPhotoDimension,
  PHOTO_DIMENSIONS,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { eventSend } from '../reducers/event/reducer';

function Photos({
  photos,
  name,
  dispatch,
}: {
  photos: IPhoto[];
  name: string;
  dispatch: Dispatch;
}): React.JSX.Element {
  const [originalImageUUID, setOriginalImageUUID] = useState<string>(
    () => photos[0]?.uuid ?? '',
  );
  const [isModalOpened, setModalOpen] = useState<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const wasModalOpenedRef = useRef<boolean>(false);

  const setModalState = useCallback(
    (isOpen: boolean): void => {
      setModalOpen(isOpen);

      dispatch(
        eventSend({
          type: EEventType.PRODUCT_PHOTO,
          action: EEventContextAction.CLICK,
          context: { isEnabled: isOpen },
        }),
      );
    },
    [dispatch],
  );

  const handleEsc = useCallback(
    (event: KeyboardEvent): void => {
      if (event.code === 'Escape') {
        setModalState(false);
      }
    },
    [setModalState],
  );

  const handleKeyDownModal = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.code === 'ArrowRight') {
        const index: number = photos.findIndex(
          ({ uuid }: IPhoto): boolean => uuid === originalImageUUID,
        );

        if (index + 1 < photos.length) {
          setOriginalImageUUID(photos[index + 1].uuid);
        }
      }

      if (event.code === 'ArrowLeft') {
        const index: number = photos.findIndex(
          ({ uuid }: IPhoto): boolean => uuid === originalImageUUID,
        );

        if (index - 1 >= 0) {
          setOriginalImageUUID(photos[index - 1].uuid);
        }
      }
    },
    [photos, originalImageUUID],
  );

  useEffect((): (() => void) => {
    window.addEventListener('keydown', handleEsc);

    return (): void => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [setModalState, handleEsc]);

  useEffect((): void => {
    if (isModalOpened) {
      wasModalOpenedRef.current = true;
      closeButtonRef.current?.focus();
    } else if (wasModalOpenedRef.current) {
      triggerButtonRef.current?.focus();
    }
  }, [isModalOpened]);

  let dimensionThumbnail: IPhotoDimension | undefined;
  let dimensionSize: IPhotoDimension | undefined;

  const images = photos.map((photo: IPhoto, index: number) => {
    const { dimension } = photo;

    if (dimension && typeof dimension !== 'string') {
      dimensionThumbnail = dimension.find(
        ({ type }: IPhotoDimension): boolean => type === '100',
      );
      dimensionSize = dimension.find(
        ({ type }: IPhotoDimension): boolean => type === '200',
      );
    }

    const srcSet: string[] = PHOTO_DIMENSIONS.map(
      (size: number): string =>
        `/static/product/photo/${photo.uuid}--${size}.webp ${size}w`,
    );

    return {
      uuid: photo.uuid,
      original: `/static/product/photo/${photo.uuid}--200.webp`,
      thumbnail: `/static/product/photo/${photo.uuid}--100.webp`,
      originalAlt: `${name} (${index + 1} of ${photos.length})`,
      thumbnailAlt: `${name} thumbnail (${index + 1} of ${photos.length})`,
      originalTitle: name,
      thumbnailTitle: `${name} thumbnail`,
      srcSet: srcSet.join(','),
      dimensionThumbnail,
      dimensionSize,
    };
  });

  let image = images.find(({ uuid }) => uuid === originalImageUUID);

  if (!image && images.length > 0) {
    [image] = images;
  }

  if (!image) {
    return <div className="photos container--7 container--t" />;
  }

  return (
    <div
      className="photos container--7 container--t"
      onKeyDown={(e): void => handleKeyDownModal(e)}
    >
      {images.length > 1 && (
        <div className="container--3 container--t">
          <div className="container--thumbnail">
            {images.map((i) => (
              <button
                type="button"
                key={i.uuid}
                className="photos--image--container"
                aria-pressed={i.uuid === originalImageUUID}
                aria-label={i.thumbnailAlt}
                onClick={(): void => setOriginalImageUUID(i.uuid)}
              >
                <img
                  src={i.thumbnail}
                  className="photos--thumbnail"
                  alt=""
                  width={i.dimensionThumbnail?.width || undefined}
                  height={i.dimensionThumbnail?.height || undefined}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="container--7 container--t main-image">
        <button
          ref={triggerButtonRef}
          type="button"
          aria-label={`${image.originalAlt} — open full size`}
          aria-haspopup="dialog"
          onClick={(): void => setModalState(true)}
        >
          <img
            src={image.original}
            srcSet={image.srcSet}
            sizes="(max-width: 767px) 100vw, (max-width: 991px) 67vw, 720px"
            className="product-image"
            alt={image.originalAlt}
            title={image.originalTitle}
            width={image.dimensionSize?.width || undefined}
            height={image.dimensionSize?.height || undefined}
            loading="eager"
            fetchPriority="high"
          />
        </button>
      </div>

      <div
        className={`modal ${isModalOpened ? 'open' : 'close'}`}
        role="dialog"
        aria-modal="true"
        aria-label={name}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className={`photos--closeIcon ${isModalOpened ? 'opened' : 'closed'}`}
          onClick={(): void => setModalState(false)}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.59-13L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z" />
          </svg>
        </button>

        <div className="container--image">
          <img
            src={image.original}
            srcSet={image.srcSet}
            className="image"
            alt={image.originalAlt}
            title={image.originalTitle}
            width={image.dimensionSize?.width || undefined}
            height={image.dimensionSize?.height || undefined}
            loading="lazy"
          />
        </div>

        <div className="container--thumbnail">
          {images.map((i) => (
            <button
              type="button"
              key={i.uuid}
              aria-pressed={i.uuid === originalImageUUID}
              aria-label={i.thumbnailAlt}
              onClick={(): void => setOriginalImageUUID(i.uuid)}
            >
              <img
                src={i.thumbnail}
                className={`photos--thumbnail ${
                  i.uuid === originalImageUUID ? 'active' : ''
                }`}
                alt=""
                title={i.thumbnailTitle}
                width={i.dimensionThumbnail?.width || undefined}
                height={i.dimensionThumbnail?.height || undefined}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Photos;
